import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useNotification } from '@/components/notifications';
import { useOrgStore } from '@/stores/org-store';
import { useWorkflowStore } from '@/stores/workflow-store';
import { websocketService } from '@/services/websocket/websocket-service';
import {
  useRunStatusPoller,
  type PollResult,
} from '@/hooks/use-run-status-poller';
import {
  useReconcileOnReconnect,
  applyReconcileFromRun,
  type WorkflowStatusToastsReconcileOptions,
} from '@/hooks/use-reconcile-on-reconnect';
import {
  NodeStatusEvent,
  TerminationEvent,
  TerminationStatusCode,
  WorkflowEventTypes,
  WorkflowExecutionUiStatus,
} from '@/types/workflow-types';
import {
  detectWorkflowErrors,
  hasExecutionSuccessEvents,
  isUserCancellation,
  shouldCheckRunStatus,
  shouldShowCompleted,
  shouldShowFailed,
  shouldShowReconnecting,
} from '@/pages/workflow/component/controls/helpers/status-helpers';

export interface UseWorkflowStatusToastsOptions {
  isExecutingWorkflow: boolean;
  isConnected: boolean;
  connectionError: string | null;
  nodeStatuses: Record<string, NodeStatusEvent[]>;
  /** Post-reconnect reconcile + terminal poller → {@link applyReconcileFromRun}. */
  reconcile: WorkflowStatusToastsReconcileOptions;
}

// ─────────────────────────────────────────────────────────────────────────
// User-stop intent (per session).
//
// The backend sends execution_result {success: false} for both genuine
// failures AND user-initiated stops, but does not always include the
// `errors.type === 'cancelled'` field that `isUserCancellation` relies on.
// When `handleStopWorkflow` / `handleStopIntervention` succeed via HTTP,
// they call `markUserStoppedRun(sessionId)` so that the status effect can
// suppress "Run Failed" — regardless of what WS events arrive afterward.
// ─────────────────────────────────────────────────────────────────────────

interface UserStoppedFlag {
  stopped: boolean;
  /**
   * Set when a pause/stop was requested but the run reached a terminal
   * state some other way first (raced by natural completion/failure).
   * Consumed once by the COMPLETED toast branch instead of the generic
   * "Run Complete" message -- see markRunCompletionOverride.
   */
  completionOverrideMessage: string | null;
}

const userStoppedBySession = new Map<string, UserStoppedFlag>();
/**
 * Stop signalled before this session's hook effect registered its flag
 * (same-tick race). Entries are only queued while a toast hook is mounted
 * for the editor's current session; otherwise nothing will consume them.
 */
const pendingUserStoppedSessions = new Map<string, number>();
/** Bound + TTL so a stray pending entry cannot grow without limit. */
const PENDING_USER_STOPPED_MAX_ENTRIES = 16;
const PENDING_USER_STOPPED_MAX_AGE_MS = 60_000;

let activeWorkflowStatusToastsHooks = 0;

function pruneStalePendingUserStoppedSessions(): void {
  const now = Date.now();
  for (const [sessionId, addedAt] of pendingUserStoppedSessions) {
    if (now - addedAt > PENDING_USER_STOPPED_MAX_AGE_MS) {
      pendingUserStoppedSessions.delete(sessionId);
    }
  }
  while (pendingUserStoppedSessions.size > PENDING_USER_STOPPED_MAX_ENTRIES) {
    const oldest = pendingUserStoppedSessions.keys().next().value;
    if (oldest === undefined) break;
    pendingUserStoppedSessions.delete(oldest);
  }
}

export function markUserStoppedRun(sessionId: string): void {
  const flag = userStoppedBySession.get(sessionId);
  if (flag) {
    flag.stopped = true;
    return;
  }

  // No toast hook → nothing to suppress; avoid leaking module-scoped state.
  if (activeWorkflowStatusToastsHooks === 0) {
    return;
  }

  const { currentSession } = useWorkflowStore.getState();
  if (currentSession !== sessionId) {
    return;
  }

  pendingUserStoppedSessions.set(sessionId, Date.now());
  pruneStalePendingUserStoppedSessions();
}

/**
 * Marks that the next "Run Complete" toast for this session should be
 * replaced with `message` -- used when a pause/stop was requested but the
 * run reached a terminal state some other way first (see the pause/stop
 * race handling in workflow-editor-page.tsx's handleTerminationEvent /
 * handleExecutionResultEvent). Unlike markUserStoppedRun, this always
 * fires well after the toast hook has mounted (in response to a WS/poll
 * terminal event, not a synchronous click), so no same-tick pending-map
 * handling is needed -- a missing flag is a no-op.
 */
export function markRunCompletionOverride(
  sessionId: string,
  message: string,
): void {
  const flag = userStoppedBySession.get(sessionId);
  if (!flag) {
    return;
  }
  flag.completionOverrideMessage = message;
}

// ─────────────────────────────────────────────────────────────────────────
// Per-hook live snapshots for in-toast counters (Reconnecting / Checking).
// Each `useWorkflowStatusToasts` instance owns a store; toast components
// read it via context so multiple editors or Strict Mode remounts do not
// share subscribers or snapshot state.
// ─────────────────────────────────────────────────────────────────────────

interface LiveReconnectSnapshot {
  isReconnecting: boolean;
  attempts: number;
  maxAttempts: number;
  enabled: boolean;
  isOffline: boolean;
}

interface LivePollSnapshot {
  count: number;
  max: number;
  exhausted: boolean;
}

interface LiveToastStore {
  refreshLiveReconnect: () => void;
  setLivePollSnapshot: (count: number, max: number, exhausted: boolean) => void;
  subscribeLiveReconnect: (cb: () => void) => () => void;
  getLiveReconnectSnapshot: () => LiveReconnectSnapshot;
  subscribeLivePoll: (cb: () => void) => () => void;
  getLivePollSnapshot: () => LivePollSnapshot;
}

function createLiveToastStore(): LiveToastStore {
  let liveReconnectSnapshot: LiveReconnectSnapshot = {
    isReconnecting: false,
    attempts: 0,
    maxAttempts: 0,
    enabled: true,
    isOffline: false,
  };
  const liveReconnectSubscribers = new Set<() => void>();

  const refreshLiveReconnect = () => {
    const status = websocketService.getReconnectStatus();
    const next: LiveReconnectSnapshot = {
      isReconnecting: status.isReconnecting,
      attempts: status.attempts,
      maxAttempts: status.maxAttempts,
      enabled: status.enabled,
      isOffline: websocketService.isOffline(),
    };
    const prev = liveReconnectSnapshot;
    if (
      prev.isReconnecting === next.isReconnecting &&
      prev.attempts === next.attempts &&
      prev.maxAttempts === next.maxAttempts &&
      prev.enabled === next.enabled &&
      prev.isOffline === next.isOffline
    ) {
      return;
    }
    liveReconnectSnapshot = next;
    liveReconnectSubscribers.forEach((cb) => cb());
  };

  let livePollSnapshot: LivePollSnapshot = {
    count: 0,
    max: 0,
    exhausted: false,
  };
  const livePollSubscribers = new Set<() => void>();

  const setLivePollSnapshot = (
    count: number,
    max: number,
    exhausted: boolean,
  ) => {
    if (
      livePollSnapshot.count === count &&
      livePollSnapshot.max === max &&
      livePollSnapshot.exhausted === exhausted
    ) {
      return;
    }
    livePollSnapshot = { count, max, exhausted };
    livePollSubscribers.forEach((cb) => cb());
  };

  return {
    refreshLiveReconnect,
    setLivePollSnapshot,
    subscribeLiveReconnect: (cb) => {
      liveReconnectSubscribers.add(cb);
      return () => {
        liveReconnectSubscribers.delete(cb);
      };
    },
    getLiveReconnectSnapshot: () => liveReconnectSnapshot,
    subscribeLivePoll: (cb) => {
      livePollSubscribers.add(cb);
      return () => {
        livePollSubscribers.delete(cb);
      };
    },
    getLivePollSnapshot: () => livePollSnapshot,
  };
}

const LiveToastStoreContext = createContext<LiveToastStore | null>(null);

function useLiveToastStore(): LiveToastStore {
  const store = useContext(LiveToastStoreContext);
  if (!store) {
    throw new Error(
      'Live toast components must be rendered within LiveToastStoreContext',
    );
  }
  return store;
}

function LiveToastStoreProvider({
  store,
  children,
}: {
  store: LiveToastStore;
  children: ReactNode;
}) {
  return (
    <LiveToastStoreContext.Provider value={store}>
      {children}
    </LiveToastStoreContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Toast message components — rendered once per main state transition;
// internal `useSyncExternalStore` subscriptions update content in place.
// ─────────────────────────────────────────────────────────────────────────

const RunReconnectingToast = () => {
  const { subscribeLiveReconnect, getLiveReconnectSnapshot } =
    useLiveToastStore();
  const r = useSyncExternalStore(
    subscribeLiveReconnect,
    getLiveReconnectSnapshot,
  );

  if (r.isOffline) {
    return (
      <span>
        Offline. Your run is still active. Will resume when back online.
      </span>
    );
  }

  // Mirrors the legacy bar's "(attempts - 1) / (maxAttempts - 1)" format —
  // the WS service treats the very first reconnect attempt as silent, so
  // the displayed count starts at 1.
  const current = Math.max(1, r.attempts - 1);
  const total = Math.max(1, r.maxAttempts - 1);
  return (
    <span>
      Reconnecting ({current}/{total})
    </span>
  );
};

const RunCheckingStatusToast = () => {
  const { subscribeLivePoll, getLivePollSnapshot } = useLiveToastStore();
  const p = useSyncExternalStore(subscribeLivePoll, getLivePollSnapshot);

  if (p.exhausted || (p.max > 0 && p.count >= p.max)) {
    return (
      <span>
        Disconnected. Run continues on server. Refresh to check status.
      </span>
    );
  }

  return (
    <span>
      Checking Status. Verifying run status ({p.count}/{p.max})...
    </span>
  );
};

/**
 * Drives transient workflow status toasts (Reconnecting / Offline,
 * Checking Status / Disconnected) using a {@link WorkflowExecutionUiStatus}
 * state machine. Terminal outcomes (complete / failed) no longer toast — the
 * canvas run-status pill surfaces them, derived from node statuses.
 *
 * Toasts are fired only on main state transitions (IDLE → RECONNECTING,
 * RECONNECTING → CHECKING_STATUS, etc.).  Within RECONNECTING and
 * CHECKING_STATUS the toast message is a React component subscribed to
 * per-instance live snapshots, so attempts and poll counters update in
 * place — the same UX the legacy bar provided — without firing a fresh
 * notification on each increment.
 */
export function useWorkflowStatusToasts({
  isExecutingWorkflow,
  isConnected,
  connectionError,
  nodeStatuses,
  reconcile,
}: UseWorkflowStatusToastsOptions): void {
  const notification = useNotification();
  const { currentWorkflow, currentSession, persistStatusHighlight } =
    useWorkflowStore();
  const { currentOrg } = useOrgStore();

  useReconcileOnReconnect({
    isConnected,
    workflowId: currentWorkflow?.id,
    sessionId: currentSession,
    orgId: currentOrg?.orgid,
    ...reconcile,
  });

  const [status, setStatus] = useState<WorkflowExecutionUiStatus>(
    WorkflowExecutionUiStatus.IDLE,
  );
  const [reconnectStatus, setReconnectStatus] = useState(
    websocketService.getReconnectStatus(),
  );
  const [hasEverConnected, setHasEverConnected] = useState(false);

  const prevIsExecutingRef = useRef(isExecutingWorkflow);
  const prevIsExecutingForStopRef = useRef(isExecutingWorkflow);
  // Guards against the status effect overriding a terminal status set by
  // the poller (terminal status applied via reconcile, not local setStatus).
  const pollerSetStatusRef = useRef(false);

  const liveStoreRef = useRef<LiveToastStore | null>(null);
  if (!liveStoreRef.current) {
    liveStoreRef.current = createLiveToastStore();
  }
  const liveStore = liveStoreRef.current;

  const userStoppedFlagRef = useRef<UserStoppedFlag | null>(null);
  if (!userStoppedFlagRef.current) {
    userStoppedFlagRef.current = {
      stopped: false,
      completionOverrideMessage: null,
    };
  }
  const userStoppedFlag = userStoppedFlagRef.current;

  // Tracks when the user explicitly dismissed the COMPLETED status by clicking
  // the canvas (persistStatusHighlight → false). Without this guard the main
  // status effect re-runs (because `status` is in its deps), sees execution
  // results still in nodeStatuses, and re-enters COMPLETED — firing a second
  // "Run Complete" toast.
  const userDismissedCompletedRef = useRef(false);
  const sessionId = currentSession ?? '';

  useEffect(() => {
    activeWorkflowStatusToastsHooks += 1;
    return () => {
      activeWorkflowStatusToastsHooks -= 1;
      if (activeWorkflowStatusToastsHooks === 0) {
        pendingUserStoppedSessions.clear();
      }
    };
  }, []);

  useEffect(() => {
    userStoppedFlag.stopped = false;
    userStoppedFlag.completionOverrideMessage = null;
    if (!sessionId) {
      return;
    }
    if (pendingUserStoppedSessions.has(sessionId)) {
      userStoppedFlag.stopped = true;
      pendingUserStoppedSessions.delete(sessionId);
    }
    userStoppedBySession.set(sessionId, userStoppedFlag);
    return () => {
      userStoppedBySession.delete(sessionId);
      pendingUserStoppedSessions.delete(sessionId);
    };
  }, [sessionId, userStoppedFlag]);

  // Tracks the last status we fired a toast for so we only fire on
  // main-state transitions.
  const prevStatusRef = useRef<WorkflowExecutionUiStatus | null>(null);

  const reconcileRef = useRef(reconcile);
  reconcileRef.current = reconcile;

  const handleTerminalPollStatus = useCallback((result: PollResult) => {
    if (!result.run) {
      console.warn(
        '[useWorkflowStatusToasts] Terminal poll missing run snapshot',
        result,
      );
      return;
    }
    pollerSetStatusRef.current = true;
    applyReconcileFromRun({ run: result.run, ...reconcileRef.current });
  }, []);

  const {
    isPolling,
    pollCount,
    pollExhausted,
    maxPollAttempts,
    startPolling,
    stopPolling,
  } = useRunStatusPoller({
    workflowId: currentWorkflow?.id,
    sessionId: currentSession,
    orgId: currentOrg?.orgid,
    onTerminalStatus: handleTerminalPollStatus,
    onPollError: () => {
      // Don't finalize — the run is likely still going on the server.
      // Keep CHECKING_STATUS with pollCount >= maxPollAttempts so the
      // toast effect emits the "Disconnected" warning instead of silently
      // failing.
    },
  });

  // Push poll state into this instance's snapshot for the CheckingStatus toast.
  useEffect(() => {
    liveStore.setLivePollSnapshot(pollCount, maxPollAttempts, pollExhausted);
  }, [liveStore, pollCount, maxPollAttempts, pollExhausted]);

  // Close any persisted COMPLETED state when the highlight is cleared
  // (e.g. user clicked the pane).  Skip while the poller just set
  // COMPLETED but the editor has not yet flipped persistStatusHighlight.
  useEffect(() => {
    if (persistStatusHighlight) {
      pollerSetStatusRef.current = false;
    }
    if (
      !persistStatusHighlight &&
      status === WorkflowExecutionUiStatus.COMPLETED &&
      !pollerSetStatusRef.current
    ) {
      userDismissedCompletedRef.current = true;
      setStatus(WorkflowExecutionUiStatus.IDLE);
    }
  }, [persistStatusHighlight, status]);

  // Start polling when entering CHECKING_STATUS; stop (and reset exhaustion)
  // when leaving so a later disconnect can poll again.
  useEffect(() => {
    if (status !== WorkflowExecutionUiStatus.CHECKING_STATUS) {
      if (isPolling || pollExhausted) {
        stopPolling();
      }
      return;
    }
    if (!isPolling && !pollExhausted) {
      startPolling();
    }
  }, [status, isPolling, pollExhausted, startPolling, stopPolling]);

  // Stop polling if WebSocket reconnects.
  useEffect(() => {
    if (isConnected && isPolling) {
      stopPolling();
    }
  }, [isConnected, isPolling, stopPolling]);

  // Track whether the WebSocket has ever connected during this execution.
  useEffect(() => {
    const prevIsLoading = prevIsExecutingRef.current;

    if (!prevIsLoading && isExecutingWorkflow) {
      setHasEverConnected(false);
    } else if (isConnected && isExecutingWorkflow) {
      setHasEverConnected(true);
    }

    if (!isExecutingWorkflow) {
      setHasEverConnected(false);
    }

    prevIsExecutingRef.current = isExecutingWorkflow;
  }, [isConnected, isExecutingWorkflow]);

  // Sync reconnect status from the singleton while disconnected mid-run.
  // Skips the 250ms interval when the socket is open to avoid idle polling.
  useEffect(() => {
    if (!isExecutingWorkflow) {
      return;
    }

    const sync = () => {
      setReconnectStatus(websocketService.getReconnectStatus());
      liveStore.refreshLiveReconnect();
    };

    sync();

    if (websocketService.isConnected()) {
      return;
    }

    const interval = setInterval(() => {
      sync();
    }, 250);
    return () => clearInterval(interval);
  }, [isExecutingWorkflow, isConnected, liveStore]);

  useEffect(() => {
    const isNewExecution =
      !prevIsExecutingForStopRef.current && isExecutingWorkflow;
    prevIsExecutingForStopRef.current = isExecutingWorkflow;

    if (isNewExecution) {
      pollerSetStatusRef.current = false;
      userDismissedCompletedRef.current = false;
      userStoppedFlag.stopped = false;
      userStoppedFlag.completionOverrideMessage = null;
      // Reset to IDLE so a back-to-back successful run still emits the
      // "Run Complete" toast (the toast effect treats same-state as a
      // no-op and the legacy bar relied on persistStatusHighlight clearing
      // status — for the toast hook we make this explicit).
      setStatus(WorkflowExecutionUiStatus.IDLE);
    }

    const workflowEvents = nodeStatuses['workflow_events'] || [];
    const terminationEvent = workflowEvents.find(
      (event) => event.event_type === WorkflowEventTypes.TERMINATION,
    ) as TerminationEvent | undefined;
    const wasCancelledByUser = isUserCancellation(nodeStatuses);

    const executionResultEvents = nodeStatuses['execution_result'] || [];
    const hasExecutionResult = executionResultEvents.length > 0;
    const hasExecutionSuccessful = hasExecutionSuccessEvents(nodeStatuses);
    const hasError = detectWorkflowErrors(nodeStatuses);
    const terminationIsError =
      terminationEvent?.status_code === TerminationStatusCode.ERROR;

    // Read directly from the singleton — React `isConnected` can lag up to
    // 1s behind the real socket (useWebSocket polls every second). Fast
    // localhost reconnects would otherwise skip Reconnecting entirely.
    const liveIsConnected = websocketService.isConnected();
    const liveReconnectStatus = websocketService.getReconnectStatus();
    const attemptsExhausted =
      liveReconnectStatus.attempts >= liveReconnectStatus.maxAttempts;

    if (
      isExecutingWorkflow &&
      !liveIsConnected &&
      websocketService.isOffline()
    ) {
      setStatus(WorkflowExecutionUiStatus.RECONNECTING);
    } else if (
      shouldShowReconnecting(
        isExecutingWorkflow,
        liveIsConnected,
        liveReconnectStatus,
      )
    ) {
      setStatus(WorkflowExecutionUiStatus.RECONNECTING);
    } else if (
      !userDismissedCompletedRef.current &&
      shouldShowCompleted(
        isExecutingWorkflow,
        hasError,
        terminationIsError,
        connectionError,
        hasExecutionSuccessful,
        terminationEvent,
        hasExecutionResult,
        wasCancelledByUser,
      )
    ) {
      setStatus(WorkflowExecutionUiStatus.COMPLETED);
    } else if (
      // User explicitly stopped the run — go to IDLE silently so "Run Failed"
      // is never shown.  "Workflow stopped successfully" from handleStopWorkflow
      // is sufficient feedback.  Three independent signals cover all timing
      // races between the HTTP response and the WS events:
      //
      // 1. userStoppedFlag: set by markUserStoppedRun(sessionId) before
      //    finalizeWorkflowExecution().  Most reliable — fires regardless of
      //    whether WS events arrive before or after.
      //
      // 2. terminationEvent STOPPED: backend sent a TERMINATION event with
      //    status_code=STOPPED before finalizeWorkflowExecution() ran.
      //
      // 3. wasCancelledByUser: execution_result includes errors.type='cancelled'.
      //    Kept as a fallback for backends that include the cancellation marker.
      (userStoppedFlag.stopped ||
        terminationEvent?.status_code === TerminationStatusCode.STOPPED ||
        wasCancelledByUser) &&
      !isExecutingWorkflow
    ) {
      setStatus(WorkflowExecutionUiStatus.IDLE);
    } else if (
      shouldShowFailed(
        liveIsConnected,
        isExecutingWorkflow,
        liveReconnectStatus,
        connectionError,
        hasError,
        terminationIsError,
        hasEverConnected,
        wasCancelledByUser,
      )
    ) {
      setStatus(WorkflowExecutionUiStatus.FAILED);
    } else if (
      // Stay in RECONNECTING during the silent first reconnect attempt
      // (singleton reports isReconnecting=false during that ~500ms-1s
      // window before the first attempt fails).  Without this, the state
      // machine would flap to CHECKING_STATUS and fire a spurious
      // "Checking Status..." toast right after the network is restored.
      // Once attempts are exhausted we fall through to the
      // CHECKING_STATUS branch below.
      status === WorkflowExecutionUiStatus.RECONNECTING &&
      isExecutingWorkflow &&
      !liveIsConnected &&
      !attemptsExhausted
    ) {
      setStatus(WorkflowExecutionUiStatus.RECONNECTING);
    } else if (
      shouldCheckRunStatus(
        liveIsConnected,
        isExecutingWorkflow,
        liveReconnectStatus,
        hasEverConnected,
        connectionError,
      )
    ) {
      setStatus(WorkflowExecutionUiStatus.CHECKING_STATUS);
    } else if (
      // WS is healthy mid-execution — leave any RECONNECTING/CHECKING_STATUS
      // state so the next connection drop fires a fresh toast.  Without
      // this, status stays stuck at RECONNECTING forever after a successful
      // reconnect (none of the legacy branches transition out during an
      // active execution), which silently swallows toasts for subsequent
      // disconnects.
      isExecutingWorkflow &&
      liveIsConnected &&
      (status === WorkflowExecutionUiStatus.RECONNECTING ||
        status === WorkflowExecutionUiStatus.CHECKING_STATUS)
    ) {
      setStatus(WorkflowExecutionUiStatus.IDLE);
    } else if (
      !isExecutingWorkflow &&
      !hasExecutionResult &&
      !hasExecutionSuccessful &&
      !terminationEvent &&
      !pollerSetStatusRef.current
    ) {
      setStatus(WorkflowExecutionUiStatus.IDLE);
    }
  }, [
    status,
    isExecutingWorkflow,
    isConnected,
    connectionError,
    nodeStatuses,
    reconnectStatus,
    hasEverConnected,
    userStoppedFlag,
  ]);

  // Dismiss handles for the persistent toasts (Reconnecting, Checking
  // Status).  Stored so we can programmatically close them when the
  // status transitions out, instead of waiting for an auto-dismiss that
  // would never come (`duration: Infinity`).
  const reconnectingToastDismissRef = useRef<(() => void) | null>(null);
  const checkingStatusToastDismissRef = useRef<(() => void) | null>(null);

  // Fire toasts on main-state transitions only.  Sub-state changes
  // (offline ↔ online, polling ↔ exhausted) and counter ticks update
  // the in-toast content via the live-snapshot subscriptions above.
  // Reconnecting / Checking Status toasts persist until manually
  // dismissed or until we transition out of their state — matching the
  // prior status bar's "stays until status changes" behaviour.
  useEffect(() => {
    const prev = prevStatusRef.current;
    if (status === prev) {
      return;
    }
    prevStatusRef.current = status;

    if (prev === WorkflowExecutionUiStatus.RECONNECTING) {
      reconnectingToastDismissRef.current?.();
      reconnectingToastDismissRef.current = null;
    }
    if (prev === WorkflowExecutionUiStatus.CHECKING_STATUS) {
      checkingStatusToastDismissRef.current?.();
      checkingStatusToastDismissRef.current = null;
    }

    if (status === WorkflowExecutionUiStatus.RECONNECTING) {
      // Make sure the snapshot the toast subscribes to is up to date
      // before the toast renders for the first time.
      liveStore.refreshLiveReconnect();
      reconnectingToastDismissRef.current = notification.warning(
        <LiveToastStoreProvider store={liveStore}>
          <RunReconnectingToast />
        </LiveToastStoreProvider>,
        { duration: Infinity, dismissible: false },
      );
    } else if (status === WorkflowExecutionUiStatus.CHECKING_STATUS) {
      liveStore.setLivePollSnapshot(pollCount, maxPollAttempts, pollExhausted);
      checkingStatusToastDismissRef.current = notification.info(
        <LiveToastStoreProvider store={liveStore}>
          <RunCheckingStatusToast />
        </LiveToastStoreProvider>,
        { duration: Infinity, dismissible: false },
      );
    } else if (status === WorkflowExecutionUiStatus.COMPLETED) {
      if (userStoppedFlag.completionOverrideMessage) {
        notification.info(userStoppedFlag.completionOverrideMessage);
        userStoppedFlag.completionOverrideMessage = null;
      }
    }
  }, [
    status,
    notification,
    pollCount,
    maxPollAttempts,
    pollExhausted,
    liveStore,
    userStoppedFlag,
  ]);

  // Clean up persistent toasts on unmount so they don't linger after
  // the user navigates away from the workflow editor.
  useEffect(() => {
    return () => {
      reconnectingToastDismissRef.current?.();
      checkingStatusToastDismissRef.current?.();
    };
  }, []);
}
